import React, { useState, useEffect, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import * as firestoreService from '../../services/firestoreService';
import { Bell, Clock, Save, WrenchIcon } from 'lucide-react';
import DateInput from '../common/DateInput';

const AppConfigView = () => {
  const { showAlert } = useContext(AppContext);

  // Alert config state
  const [alertMessage, setAlertMessage] = useState('');
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [alertExpiry, setAlertExpiry] = useState('');
  const [alertCreatedAt, setAlertCreatedAt] = useState(null);
  const [alertSaving, setAlertSaving] = useState(false);

  // Trial config state
  const [trialMonths, setTrialMonths] = useState(6);
  const [trialSaving, setTrialSaving] = useState(false);

  // Maintenance config state
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMinutes, setMaintenanceMinutes] = useState('');
  const [maintenanceMinutesError, setMaintenanceMinutesError] = useState('');
  const [maintenanceSaving, setMaintenanceSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      const [alertCfg, trialCfg, maintCfg] = await Promise.all([
        firestoreService.getAppConfig('systemAlert'),
        firestoreService.getAppConfig('trialConfig'),
        firestoreService.getAppConfig('maintenance'),
      ]);
      if (alertCfg) {
        setAlertMessage(alertCfg.message || '');
        setAlertEnabled(alertCfg.enabled || false);
        setAlertExpiry(alertCfg.expiresAt ? alertCfg.expiresAt.slice(0, 10) : '');
        setAlertCreatedAt(alertCfg.createdAt || null);
      }
      if (trialCfg) {
        setTrialMonths(trialCfg.defaultTrialMonths ?? 6);
      }
      if (maintCfg) {
        setMaintenanceEnabled(maintCfg.enabled || false);
        setMaintenanceMinutes(maintCfg.minutes != null ? String(maintCfg.minutes) : '');
      }
    };
    load();
  }, []);

  const handleSaveAlert = async () => {
    setAlertSaving(true);
    try {
      const now = new Date().toISOString();
      const createdAt = alertCreatedAt || now;
      await firestoreService.setAppConfig('systemAlert', {
        message: alertMessage.trim(),
        enabled: alertEnabled,
        expiresAt: alertExpiry ? new Date(alertExpiry + 'T23:59:59').toISOString() : null,
        createdAt,
        updatedAt: now,
      });
      if (!alertCreatedAt) setAlertCreatedAt(createdAt);
      showAlert('Alert configuration saved.', 'Saved');
    } catch (err) {
      showAlert('Failed to save alert config.', 'Error');
    } finally {
      setAlertSaving(false);
    }
  };

  const handleNewAlert = () => {
    // Reset createdAt so next save generates a fresh timestamp, causing all users to see it again
    setAlertCreatedAt(null);
    showAlert('createdAt reset — saving this alert will send it to all users on their next idle return.', 'Info');
  };

  const handleSaveMaintenance = async () => {
    // Validate minutes
    if (maintenanceMinutes !== '') {
      const mins = Number(maintenanceMinutes);
      if (!Number.isInteger(mins) || mins <= 0) {
        setMaintenanceMinutesError('Must be a positive whole number or left blank.');
        return;
      }
    }
    setMaintenanceMinutesError('');
    setMaintenanceSaving(true);
    try {
      const minutes = maintenanceMinutes !== '' ? parseInt(maintenanceMinutes) : null;
      await firestoreService.setAppConfig('maintenance', {
        enabled: maintenanceEnabled,
        minutes,
        enabledAt: maintenanceEnabled ? new Date().toISOString() : null,
      });
      showAlert(maintenanceEnabled ? 'Maintenance mode enabled.' : 'Maintenance mode disabled.', 'Saved');
    } catch (err) {
      showAlert('Failed to save maintenance config.', 'Error');
    } finally {
      setMaintenanceSaving(false);
    }
  };

  const handleSaveTrial = async () => {
    setTrialSaving(true);
    try {
      await firestoreService.setAppConfig('trialConfig', {
        defaultTrialMonths: parseInt(trialMonths) || 0,
      });
      showAlert('Trial configuration saved. Applies to new signups only.', 'Saved');
    } catch (err) {
      showAlert('Failed to save trial config.', 'Error');
    } finally {
      setTrialSaving(false);
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-white mb-8">App Configuration</h1>

      {/* System Alert */}
      <div className="bg-black rounded-lg border border-gray-700 p-6 mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <Bell className="w-5 h-5 text-yellow-400" />
            <h2 className="text-xl font-bold text-white">System Alert</h2>
          </div>
          {alertEnabled && (
            <span className="text-xs font-semibold px-2 py-0.5 bg-yellow-600/20 text-yellow-400 border border-yellow-600/40 rounded">
              ACTIVE
            </span>
          )}
        </div>
        <p className="text-gray-400 text-sm mb-4">
          When enabled, this message is shown to users on their first visit after 30+ minutes of inactivity.
          Update the message and save to re-broadcast to all users, or click "New Alert" to reset.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-1">Alert Message</label>
            <textarea
              value={alertMessage}
              onChange={e => setAlertMessage(e.target.value)}
              rows={3}
              placeholder="Enter your alert message..."
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white resize-none focus:ring-2 focus:ring-[#22c55e]"
            />
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="alertEnabled"
              checked={alertEnabled}
              onChange={e => setAlertEnabled(e.target.checked)}
              className="w-4 h-4 accent-[#16a34a]"
            />
            <label htmlFor="alertEnabled" className="text-sm font-medium text-white cursor-pointer">
              Enable alert
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Expiration Date
              <span className="text-gray-400 font-normal ml-1">(optional — leave blank for no expiration)</span>
            </label>
            <div className="flex items-center space-x-2">
              <DateInput
                value={alertExpiry}
                onChange={setAlertExpiry}
                className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-[#22c55e]"
              />
              {alertExpiry && (
                <button onClick={() => setAlertExpiry('')} className="text-xs text-gray-400 hover:text-white">
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleSaveAlert}
              disabled={alertSaving}
              className="flex items-center space-x-2 px-4 py-2 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              <span>{alertSaving ? 'Saving...' : 'Save Alert'}</span>
            </button>
            <button
              onClick={handleNewAlert}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 text-sm"
            >
              New Alert
            </button>
          </div>
        </div>
      </div>

      {/* Site Maintenance */}
      <div className="bg-black rounded-lg border border-gray-700 p-6 mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <WrenchIcon className="w-5 h-5 text-orange-400" />
            <h2 className="text-xl font-bold text-white">Site Maintenance</h2>
          </div>
          {maintenanceEnabled && (
            <span className="text-xs font-semibold px-2 py-0.5 bg-orange-600/20 text-orange-400 border border-orange-600/40 rounded">
              ACTIVE
            </span>
          )}
        </div>
        <p className="text-gray-400 text-sm mb-4">
          When enabled, non-admin users see a maintenance page. Admins can still access the app normally.
          Set a duration to auto-disable, or leave blank to disable manually.
        </p>

        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="maintenanceEnabled"
              checked={maintenanceEnabled}
              onChange={e => setMaintenanceEnabled(e.target.checked)}
              className="w-4 h-4 accent-[#16a34a]"
            />
            <label htmlFor="maintenanceEnabled" className="text-sm font-medium text-white cursor-pointer">
              Enable maintenance mode
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-1">
              Duration (minutes)
              <span className="text-gray-400 font-normal ml-1">(optional — leave blank to disable manually)</span>
            </label>
            <input
              type="number"
              value={maintenanceMinutes}
              onChange={e => { setMaintenanceMinutes(e.target.value); setMaintenanceMinutesError(''); }}
              min="1"
              placeholder="e.g. 30"
              className="w-32 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-[#22c55e]"
            />
            {maintenanceMinutesError && (
              <p className="text-red-400 text-xs mt-1">{maintenanceMinutesError}</p>
            )}
          </div>

          <button
            onClick={handleSaveMaintenance}
            disabled={maintenanceSaving}
            className="flex items-center space-x-2 px-4 py-2 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{maintenanceSaving ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
      </div>

      {/* Default Trial Duration */}
      <div className="bg-black rounded-lg border border-gray-700 p-6">
        <div className="flex items-center space-x-2 mb-2">
          <Clock className="w-5 h-5 text-yellow-400" />
          <h2 className="text-xl font-bold text-white">Default Trial Duration</h2>
        </div>
        <p className="text-gray-400 text-sm mb-4">
          New users automatically receive Caddie Pro for this many months at signup. Set to 0 to disable.
        </p>

        <div className="flex items-end space-x-4">
          <div>
            <label className="block text-sm font-medium text-white mb-1">Months</label>
            <input
              type="number"
              value={trialMonths}
              onChange={e => setTrialMonths(e.target.value)}
              min="0"
              max="24"
              className="w-24 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-[#22c55e]"
            />
          </div>
          <button
            onClick={handleSaveTrial}
            disabled={trialSaving}
            className="flex items-center space-x-2 px-4 py-2 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{trialSaving ? 'Saving...' : 'Save'}</span>
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">Changes apply to new signups only. Existing users are not affected.</p>
      </div>
    </div>
  );
};

export default AppConfigView;
